import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { API } from '../api/api';
import DesktopFooter from './DesktopFooter';

const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const sign = isNegative ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

// Helper function to format percentage with color
const formatPercentage = (percent) => {
  const isPositive = percent >= 0;
  const color = isPositive ? 'text-green-600' : 'text-white';
  const sign = isPositive ? '+' : '';
  return <span className={color}>{sign}{percent.toFixed(1)}%</span>;
};

export default function MobileFooter({
  totalsCurrentView,
  totalsCurrentViewPeople,
  totalsYTD,
  currentView,
  currentDate,
  customRangeData,
  isLoadingCustomRange,
  onCustomDateRangeClick,
  onClearCustomRange,
  totalsYTDPeople,
  events,
  isOutlookSyncEvent,
  isBillableEvent,
  mutationAtRef,
  showGamification = true,
  projects,
}) {
  const [bonusMode, setBonusMode] = useState('percent'); // 'percent' | 'hours'
  const [showBonusSettings, setShowBonusSettings] = useState(false);
  // Bonussen regnes i paymaster (samme formel for alle, ferie og sygdom tæller som normdage). Footeren viser kun tallene.
  const [bonus, setBonus] = useState(null);
  useEffect(() => {
    let annulleret = false;
    const hent = () => API.getMyBonus().then((r) => { if (!annulleret) setBonus(r?.bonus ?? null); }).catch(() => {});
    hent();
    const id = setInterval(hent, 10 * 60 * 1000);
    return () => { annulleret = true; clearInterval(id); };
  }, []);

  // NEW: determine which context owns the active custom range
  const customContext = customRangeData?.range?.context || null;

  const bonusStats = bonus ? { actualHours: bonus.faktiskT, expectedSoFar: bonus.forventetT, bonusPercent: bonus.procentTilDato } : null;
  const displayStats = bonusStats;
  const displayBonusPercent = displayStats?.bonusPercent;
  const bonusBtnClass = 'bg-[#FF7800] hover:bg-[#e06800]';

  // Forklaring i stedet for den gamle periodeopsætning: normen og perioderne styres i paymaster.
  const renderBonusPeriodSettings = () => (
    <div lang="en-GB" className="text-xs bg-[#111e2e] border border-[#273C61] rounded p-2 space-y-1 text-white" style={{ minWidth: 360 }}>
      <div>Calculated in paymaster, the same way for everyone: approved hours plus leave (holiday and sickness count as norm days, so leave does not affect your bonus).</div>
      {bonus && (
        <div className="text-white/60">
          Weekly norm {bonus.ugenorm}h. Data up to {bonus.dataTilOgMed}, fetched {moment(bonus.hentetAt).format('D MMM HH:mm')}.
        </div>
      )}
      <div className="text-white/40 italic">Changes of weekly norm or periods are made in paymaster.</div>
    </div>
  );

  // Helper to render custom range data
  const renderCustomRangeRow = (isDesktop = false) => {
    if (!customRangeData) return null;

    const current = customRangeData.current;
    const previous = customRangeData.previous;
    const differences = customRangeData.differences;
    const hasComparison = previous && differences;

    return (
      <div className={`flex items-center ${isDesktop ? 'gap-2' : 'justify-between'}`}>
        <div className="flex items-center gap-2">
          <button
            onClick={onClearCustomRange}
            className={`${
              isDesktop ? 'w-28' : 'w-24'
            } px-2 py-1 bg-[#FF7800] hover:bg-[#e06800] text-white text-${isDesktop ? 'sm' : 'xs'} font-bold text-center rounded transition-colors`}
            title="Click to remove custom range"
          >
            {customRangeData.label}
          </button>
          {isDesktop && (
            <span className="text-xs text-white/40">
              ({moment(customRangeData.range.start).format('D MMM')} - {moment(customRangeData.range.end).format('D MMM')})
            </span>
          )}
        </div>
        
        <div className={`${isDesktop ? 'text-sm' : 'text-xs'}`}>
          {isDesktop ? (
            // Desktop: Single line with full info
            <span>
              <strong>{formatHours(current.total)}</strong>
              {' '}({formatHours(current.billable)} bill.)
              {hasComparison && (
                <span className="text-white/70">
                  {' '}| vs last year: {differences.total >= 0 ? '+' : ''}{formatHours(differences.total)} ({formatPercentage(differences.totalPercent)}) - was {formatHours(previous.total)}
                </span>
              )}
            </span>
          ) : (
            // Mobile: Compact two-line format
            <div>
              <div>
                <strong>{formatHours(current.total)}</strong>
                {' '}({formatHours(current.billable)} bill.)
              </div>
              {hasComparison && (
                <div className="text-white/60 mt-0.5">
                  vs last year: {differences.total >= 0 ? '+' : ''}{formatHours(differences.total)} ({formatPercentage(differences.totalPercent)}) | was {formatHours(previous.total)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Footer - Compact - Fixed over address bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-3 bg-[#0D1A27] border-t border-[#273C61] z-[2000] text-white">
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="px-2 py-1 bg-[#273C61] text-white text-xs font-bold rounded">
              {currentView === 'day' ? moment(currentDate).format('D ddd') : 'Current View'}
            </span>
            <span className="text-xs">
              <strong>{formatHours(totalsCurrentView.billable + totalsCurrentView.nonBillable)}</strong> 
              {' '}({formatHours(totalsCurrentView.billable)} bill.)
            </span>
          </div>

          {/* NEW: Current View People (only for ZRM and when there is People time) */}
          {totalsCurrentViewPeople && (totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable) > 0 && (
            <div className="flex items-center justify-between">
              <span className="px-2 py-1 bg-[#273C61] text-white text-xs font-bold rounded">
                Current View People
              </span>
              <span className="text-xs">
                <strong>{formatHours(totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable)}</strong>
                {' '}({formatHours(totalsCurrentViewPeople.billable)} bill.)
              </span>
            </div>
          )}

          {/* Custom Range or YTD (Projects only) */}
          {customRangeData && customContext === 'projects' ? (
            renderCustomRangeRow(false)
          ) : (
            <div className="flex items-center justify-between">
              <button
                onClick={() => onCustomDateRangeClick('projects')}
                className="px-2 py-1 bg-[#273C61] hover:bg-[#205799] text-white text-xs font-bold rounded transition-colors"
                title="Click to select custom period"
              >
                Year-to-Date
              </button>
              <span className="text-xs">
                <strong>{formatHours(totalsYTD.billable + totalsYTD.nonBillable)}</strong>
                {' '}({formatHours(totalsYTD.billable)} bill.)
              </span>
            </div>
          )}

          {/* NEW: YTD People (mobile) OR Custom Range (People) */}
          {(
            customRangeData && customContext === 'people'
              ? renderCustomRangeRow(false)
              : (totalsYTDPeople && (totalsYTDPeople.billable + totalsYTDPeople.nonBillable) > 0 && (
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => onCustomDateRangeClick('people')}
                      className="px-2 py-1 bg-[#273C61] hover:bg-[#205799] text-white text-xs font-bold rounded transition-colors"
                      title="Click to select custom period (People)"
                    >
                      YTD People
                    </button>
                    <span className="text-xs">
                      <strong>{formatHours(totalsYTDPeople.billable + totalsYTDPeople.nonBillable)}</strong>
                      {' '}({formatHours(totalsYTDPeople.billable)} bill.)
                    </span>
                  </div>
                ))
          )}

          {/* Bonus % (ZRM only) */}
          {bonusStats && (
            <>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setBonusMode(m => m === 'percent' ? 'hours' : 'percent')}
                  className={`px-2 py-1 ${bonusBtnClass} text-white text-xs font-bold rounded transition-colors`}
                  title="Click to toggle between % and hours view"
                >
                  Bonus %
                </button>
                <span className="flex items-center gap-1 text-xs">
                  {bonusMode === 'percent' ? (
                    formatPercentage(displayBonusPercent)
                  ) : (
                    <span>
                      <strong>{formatHours(displayStats.actualHours)}</strong>
                      {' '}/ {formatHours(displayStats.expectedSoFar)} exp.
                    </span>
                  )}
                  <button
                    onClick={() => setShowBonusSettings(s => !s)}
                    className="ml-1 text-white/50 hover:text-white"
                    title="Configure bonus calculation"
                  >
                    ⚙
                  </button>
                </span>
              </div>
              {showBonusSettings && renderBonusPeriodSettings()}
            </>
          )}
        </div>
      </div>

      {/* Tablet Footer - Medium - Fixed */}
      <div className="hidden md:block lg:hidden fixed bottom-0 left-0 right-0 p-3 bg-[#0D1A27] border-t border-[#273C61] z-[2000] text-white">
        <div className="flex flex-col gap-1.5 text-sm">

          {/* Row 1: Current View items */}
          <div className="flex items-center gap-4 whitespace-nowrap">
            {/* Current View */}
            <div className="flex items-center gap-3">
              <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-[#273C61] text-white text-sm font-bold text-center rounded">
                {currentView === 'day' ? moment(currentDate).format('D ddd') : 'Current'}
              </span>
              <span>
                Total: <strong>{formatHours(totalsCurrentView.billable + totalsCurrentView.nonBillable)}</strong> -
                Billable: <strong>{formatHours(totalsCurrentView.billable)}</strong>
              </span>
            </div>

            {/* Current View People */}
            {totalsCurrentViewPeople && (totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable) > 0 && (
              <div className="flex items-center gap-3">
                <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-[#273C61] text-white text-sm font-bold text-center rounded">
                  People
                </span>
                <span>
                  Total: <strong>{formatHours(totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable)}</strong> -
                  Billable: <strong>{formatHours(totalsCurrentViewPeople.billable)}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Row 2: YTD + Bonus */}
          <div className="flex items-center gap-4 whitespace-nowrap overflow-x-auto">
            {/* Year-to-Date (Projects) OR Custom Range (Projects) */}
            {customRangeData && customContext === 'projects'
              ? renderCustomRangeRow(true)
              : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onCustomDateRangeClick('projects')}
                    className="w-24 px-2 py-1 bg-[#273C61] hover:bg-[#205799] text-white text-sm font-bold text-center rounded transition-colors"
                    title="Click to select custom period"
                  >
                    YTD
                  </button>
                  <span>
                    <strong>{formatHours(totalsYTD.billable + totalsYTD.nonBillable)}</strong>
                    {' '}({formatHours(totalsYTD.billable)} bill.)
                  </span>
                </div>
              )
            }

            {/* YTD People OR Custom Range (People) */}
            {(
              customRangeData && customContext === 'people'
                ? renderCustomRangeRow(true)
                : (totalsYTDPeople && (totalsYTDPeople.billable + totalsYTDPeople.nonBillable) > 0 && (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onCustomDateRangeClick('people')}
                        className="w-24 px-2 py-1 bg-[#273C61] hover:bg-[#205799] text-white text-sm font-bold text-center rounded transition-colors"
                        title="Click to select custom period (People)"
                      >
                        YTD People
                      </button>
                      <span>
                        Total: <strong>{formatHours(totalsYTDPeople.billable + totalsYTDPeople.nonBillable)}</strong> -
                        Billable: <strong>{formatHours(totalsYTDPeople.billable)}</strong>
                      </span>
                    </div>
                  ))
            )}

            {/* Bonus % (ZRM only) - Tablet */}
            {bonusStats && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setBonusMode(m => m === 'percent' ? 'hours' : 'percent')}
                  className={`w-24 px-2 py-1 ${bonusBtnClass} text-white text-sm font-bold text-center rounded transition-colors`}
                  title="Click to toggle between % and hours view"
                >
                  Bonus %
                </button>
                <span className="flex items-center gap-1">
                  {bonusMode === 'percent' ? (
                    formatPercentage(displayBonusPercent)
                  ) : (
                    <span>
                      <strong>{formatHours(displayStats.actualHours)}</strong>
                      {' '}/ {formatHours(displayStats.expectedSoFar)} exp.
                    </span>
                  )}
                  <button
                    onClick={() => setShowBonusSettings(s => !s)}
                    className="ml-1 text-white/50 hover:text-white text-sm"
                    title="Configure bonus calculation"
                  >
                    ⚙
                  </button>
                </span>
                {showBonusSettings && renderBonusPeriodSettings()}
              </div>
            )}

            {isLoadingCustomRange && (
              <div className="text-xs text-white/60">
                Calculating custom period...
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Desktop Footer med rustningsstige */}
      {showGamification && (
        <DesktopFooter
          events={events}
          totalsCurrentView={totalsCurrentView}
          totalsCurrentViewPeople={totalsCurrentViewPeople}
          totalsYTD={totalsYTD}
          totalsYTDPeople={totalsYTDPeople}
          currentView={currentView}
          currentDate={currentDate}
          customRangeData={customRangeData}
          isLoadingCustomRange={isLoadingCustomRange}
          onCustomDateRangeClick={onCustomDateRangeClick}
          onClearCustomRange={onClearCustomRange}
          bonus={bonus}
          renderBonusPeriodSettings={renderBonusPeriodSettings}
          isOutlookSyncEvent={isOutlookSyncEvent}
          isBillableEvent={isBillableEvent}
          mutationAtRef={mutationAtRef}
          projects={projects}
        />
      )}

      {/* Desktop Footer - Wide - Fixed (uden gamification) */}
      {!showGamification && (
      <div className="hidden lg:block fixed bottom-0 left-0 right-0 p-3 bg-[#0D1A27] border-t border-[#273C61] z-[2000] text-white">
        <div className="flex flex-col gap-1.5 text-sm">

          {/* Row 1: Current View items */}
          <div className="flex items-center gap-6 whitespace-nowrap">
            {/* Current View */}
            <div className="flex items-center gap-2">
              <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-[#273C61] text-white text-sm font-bold text-center rounded">
                Current View
              </span>
              <span className="text-sm">
                <span className="text-sm font-bold">{currentView === 'day' && `${moment(currentDate).format('D ddd')} - `}</span>
                Total: <strong>{formatHours(totalsCurrentView.billable + totalsCurrentView.nonBillable)}</strong> - Billable: <strong>{formatHours(totalsCurrentView.billable)}</strong>
              </span>
            </div>

            {/* Current View People */}
            {totalsCurrentViewPeople && (totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable) > 0 && (
              <div className="flex items-center gap-2">
                <span className="inline-flex w-auto shrink-0 whitespace-nowrap px-2 py-1 bg-[#273C61] text-white text-sm font-bold text-center rounded">
                  Current View People
                </span>
                <span className="text-sm">
                  Total: <strong>{formatHours(totalsCurrentViewPeople.billable + totalsCurrentViewPeople.nonBillable)}</strong> - Billable: <strong>{formatHours(totalsCurrentViewPeople.billable)}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Row 2: YTD + Bonus */}
          <div className="flex items-center gap-6 whitespace-nowrap overflow-x-auto">
            {/* Year-to-Date (Projects) OR Custom Range (Projects) */}
            {customRangeData && customContext === 'projects'
              ? renderCustomRangeRow(true)
              : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onCustomDateRangeClick('projects')}
                    className="w-28 px-2 py-1 bg-[#273C61] hover:bg-[#205799] text-white text-sm font-bold text-center rounded transition-colors"
                    title="Click to select custom period and compare with last year"
                  >
                    Year-to-Date
                  </button>
                  <span className="text-sm">
                    Total: <strong>{formatHours(totalsYTD.billable + totalsYTD.nonBillable)}</strong> - Billable: <strong>{formatHours(totalsYTD.billable)}</strong>
                  </span>
                </div>
              )
            }

            {/* YTD People OR Custom Range (People) */}
            {(
              customRangeData && customContext === 'people'
                ? renderCustomRangeRow(true)
                : (totalsYTDPeople && (totalsYTDPeople.billable + totalsYTDPeople.nonBillable) > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onCustomDateRangeClick('people')}
                        className="w-28 px-2 py-1 bg-[#273C61] hover:bg-[#205799] text-white text-sm font-bold text-center rounded transition-colors"
                        title="Click to select custom period (People)"
                      >
                        YTD People
                      </button>
                      <span>
                        Total: <strong>{formatHours(totalsYTDPeople.billable + totalsYTDPeople.nonBillable)}</strong> -
                        Billable: <strong>{formatHours(totalsYTDPeople.billable)}</strong>
                      </span>
                    </div>
                  ))
            )}

            {/* Bonus % (ZRM only) - Desktop */}
            {bonusStats && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setBonusMode(m => m === 'percent' ? 'hours' : 'percent')}
                  className={`w-28 px-2 py-1 ${bonusBtnClass} text-white text-sm font-bold text-center rounded transition-colors`}
                  title="Click to toggle between % and hours view"
                >
                  Bonus %
                </button>
                <span className="flex items-center gap-1 text-sm">
                  {bonusMode === 'percent' ? (
                    formatPercentage(displayBonusPercent)
                  ) : (
                    <span>
                      <strong>{formatHours(displayStats.actualHours)}</strong>
                      {' '}/ {formatHours(displayStats.expectedSoFar)} exp.
                    </span>
                  )}
                  <button
                    onClick={() => setShowBonusSettings(s => !s)}
                    className="ml-1 text-white/50 hover:text-white"
                    title="Configure bonus calculation"
                  >
                    ⚙
                  </button>
                </span>
                {showBonusSettings && renderBonusPeriodSettings()}
              </div>
            )}

            {isLoadingCustomRange && (
              <div className="text-sm text-white/60">
                Calculating custom period...
              </div>
            )}
          </div>

        </div>
      </div>
      )}
    </>
  );
}
